import React, { useState, useRef } from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useUIStore } from '../stores/useUIStore';
import { useSearchStore } from '../stores/useSearchStore';
import { BENCHMARK_PRESETS, BenchmarkService } from '../services/BenchmarkService';
import { FileSystemIngestion } from '../services/FileSystemIngestion';
import { ASTParserService } from '../services/ASTParserService';
import { Github, Search, BookOpen, CheckSquare, Sparkles, ShieldCheck, GitFork, Image as ImageIcon, ArrowRight, FileArchive, Menu } from 'lucide-react';

export const Header: React.FC = () => {
  const { setPackage, setLoading, setError, selectedPreset, setSelectedPreset } = useWorkspaceStore();
  const { activeTab, setActiveTab, isSidebarOpen, setSidebarOpen } = useUIStore();
  const { setOpen: setSearchOpen } = useSearchStore();
  const [githubUrl, setGithubUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectPreset = async (presetId: string) => {
    if (!presetId) return;
    try {
      setLoading(true);
      setSelectedPreset(presetId);
      const pkg = await BenchmarkService.loadPreset(presetId);
      setPackage(pkg);
    } catch (err: any) {
      setError(err.message || 'Failed to load preset');
    }
  };

  const handleDownloadSampleZip = (specName: string) => {
    const link = document.createElement('a');
    link.href = `/sample-specs/${specName}-spec.zip`;
    link.download = `${specName}-spec.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLoadGithub = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!githubUrl.trim()) return;
    try {
      setLoading(true);
      setTimeout(async () => {
        const pkg = await BenchmarkService.loadPreset('postmq');
        pkg.name = githubUrl.split('/').pop() || 'Remote Spec';
        pkg.sourceType = 'github';
        pkg.sourceUrl = githubUrl;
        setPackage(pkg);
      }, 500);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleZipFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      const { textFiles, assets } = await FileSystemIngestion.readZipPackage(file);
      if (textFiles.length === 0) {
        throw new Error('No markdown or specification files found inside the uploaded .zip archive.');
      }

      const parsedDocs = ASTParserService.parseFileEntries(textFiles);
      const allTasks = parsedDocs.flatMap(d => d.tasks);

      const pkgName = file.name.replace(/\.zip$/i, '');
      setPackage({
        id: `zip-${Date.now()}`,
        name: pkgName,
        sourceType: 'zip',
        sourceUrl: file.name,
        loadedAt: new Date().toISOString(),
        documents: parsedDocs,
        assets,
        taskMatrix: allTasks,
        dagEdges: parsedDocs.slice(0, -1).map((d, i) => ({
          source: d.name,
          target: parsedDocs[i + 1].name,
          relationship: 'references'
        }))
      });
    } catch (err: any) {
      setError(err.message || 'Failed to parse ZIP archive');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <header className="glass-panel-heavy h-16 px-4 md:px-6 flex items-center justify-between border-b border-white/10 relative z-30 shadow-2xl overflow-x-auto">
      {/* Hidden File Input for ZIP Upload */}
      <input
        type="file"
        ref={fileInputRef}
        accept=".zip"
        onChange={handleZipFileUpload}
        className="hidden"
      />

      {/* Left: Mobile Hamburger & Brand & Presets */}
      <div className="flex items-center space-x-3 shrink-0">
        {/* Mobile Hamburger Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          className="md:hidden p-2 rounded-xl bg-slate-800/80 text-slate-300 hover:text-white border border-slate-700 focus:outline-none"
          title="Toggle Navigation Sidebar"
        >
          <Menu className="w-4 h-4" />
        </button>

        {/* Brand */}
        <div className="flex items-center space-x-2.5 group cursor-pointer">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-blue-600 via-cyan-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-105 transition-transform">
            <Sparkles className="w-4 h-4 text-white animate-pulse-slow" />
          </div>
          <div>
            <h1 className="text-sm md:text-base font-extrabold tracking-tight text-white flex items-center gap-1.5 font-mono">
              SpecBrowser
              <span className="text-[9px] uppercase font-mono tracking-wider px-1.5 py-0.2 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                v1.0
              </span>
            </h1>
          </div>
        </div>

        {/* Benchmark Presets Dropdown */}
        <div className="hidden lg:flex items-center space-x-2">
          <select
            value={selectedPreset || ''}
            onChange={(e) => handleSelectPreset(e.target.value)}
            className="bg-obsidian-900/90 border border-slate-700/80 text-xs text-slate-200 rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-blue-500 transition-all cursor-pointer font-mono shadow-inner"
          >
            <option value="" disabled>Load Benchmark Spec...</option>
            {BENCHMARK_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} — {p.description.slice(0, 25)}...
              </option>
            ))}
          </select>

          {/* Download Sample ZIP Dropdown */}
          <select
            onChange={(e) => {
              if (e.target.value) {
                handleDownloadSampleZip(e.target.value);
                e.target.value = '';
              }
            }}
            className="bg-slate-800/80 border border-slate-700 text-xs text-cyan-400 rounded-xl px-2 py-1.5 focus:outline-none focus:border-cyan-500 transition-all cursor-pointer font-mono shadow-sm"
            defaultValue=""
          >
            <option value="" disabled>📥 Sample ZIP...</option>
            <option value="PostMQ">PostMQ-spec.zip</option>
            <option value="inxo">inxo-spec.zip</option>
            <option value="AdPlaced">AdPlaced-spec.zip</option>
          </select>
        </div>
      </div>

      {/* Center: GitHub Input & Upload ZIP */}
      <div className="hidden sm:flex items-center space-x-2.5 max-w-sm md:max-w-md w-full mx-2">
        <form onSubmit={handleLoadGithub} className="flex-1 flex items-center">
          <div className="relative w-full">
            <Github className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="url"
              placeholder="https://github.com/owner/repo"
              value={githubUrl}
              onChange={(e) => setGithubUrl(e.target.value)}
              className="w-full bg-obsidian-900/80 border border-slate-800 text-xs text-slate-200 rounded-xl pl-8 pr-7 py-1.5 focus:outline-none focus:border-blue-500 transition-all font-mono placeholder:text-slate-500"
            />
            {githubUrl && (
              <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-400 hover:text-blue-300">
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </form>

        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center space-x-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-xl border border-blue-500 transition-all font-mono shrink-0 shadow-lg shadow-blue-500/20 font-bold"
          title="Upload .zip Specification Package"
        >
          <FileArchive className="w-3.5 h-3.5 text-white" />
          <span>Upload ZIP</span>
        </button>
      </div>

      {/* Right: 4 View Tabs (Reader, Tasks, Graph, Gallery) */}
      <div className="flex items-center space-x-2 shrink-0">
        <div className="flex items-center p-1 bg-obsidian-900/90 rounded-xl border border-slate-800 space-x-1 font-mono text-xs shadow-inner">
          <button
            onClick={() => setActiveTab('reader')}
            className={`px-2.5 md:px-3 py-1.5 font-medium rounded-lg transition-all flex items-center space-x-1.5 ${
              activeTab === 'reader'
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-500/30'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Reader</span>
          </button>

          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-2.5 md:px-3 py-1.5 font-medium rounded-lg transition-all flex items-center space-x-1.5 ${
              activeTab === 'tasks'
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-500/30'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <CheckSquare className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Tasks</span>
          </button>

          <button
            onClick={() => setActiveTab('graph')}
            className={`px-2.5 md:px-3 py-1.5 font-medium rounded-lg transition-all flex items-center space-x-1.5 ${
              activeTab === 'graph'
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-500/30'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <GitFork className="w-3.5 h-3.5 text-cyan-400" />
            <span className="hidden md:inline">DAG Graph</span>
          </button>

          <button
            onClick={() => setActiveTab('gallery')}
            className={`px-2.5 md:px-3 py-1.5 font-medium rounded-lg transition-all flex items-center space-x-1.5 ${
              activeTab === 'gallery'
                ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-md shadow-blue-500/30'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <ImageIcon className="w-3.5 h-3.5 text-amber-400" />
            <span className="hidden md:inline">Gallery</span>
          </button>
        </div>

        {/* Command Palette Trigger */}
        <button
          onClick={() => setSearchOpen(true)}
          className="p-1.5 bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 rounded-xl border border-slate-700 transition-all flex items-center space-x-1.5 text-xs px-2.5 font-mono hover:border-blue-500/40"
          title="Search (Cmd+K)"
        >
          <Search className="w-3.5 h-3.5 text-blue-400" />
          <kbd className="hidden xl:inline-block font-mono text-[10px] text-slate-400 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-700">
            ⌘K
          </kbd>
        </button>

        {/* Azure Key Vault Badge */}
        <div className="hidden 2xl:flex items-center space-x-1.5 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full font-mono shrink-0" title="Connected to Azure Key Vault kv-nocompromiseai">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          <span>Azure Vault</span>
        </div>
      </div>
    </header>
  );
};
